import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import pick from 'lodash/pick';
import { introspectionApi, queryApi } from 'da-graphql-client/web';
import qs from 'qs';
const { fetchRootTypeNames, fetchTypesByName, getMaxVersionForBaseName } = introspectionApi;
const {
    fetchEedmLookupValues,
    fetchEedmValuesByAttribute,
    fetchEedmValuesByIds,
} = queryApi;
const ellucianThemeEditor = window.PRELOADED_STATE?.api?.ellucianThemeEditor || 'https://theme.elluciancloud.com';

export const handleError = async (errors, isDataAccessError = false) => {
    if(errors?.status === 401) {
        window.location.href = '/unauthorized';
    }
    if (Array.isArray(errors) && errors.length > 0) {
        errors = errors[0];
    }
    else if (typeof errors?.json === 'function') {
        errors = await errors?.json();
    }
    const code = isDataAccessError ? (errors?.extensions?.code ? errors.extensions.code : '') : errors.status;
    const name = errors?.name ? errors.name : '';
    const message = errors?.message || errors?.error || '';
    const formatMessageId = isDataAccessError ? handleDataAccessErrors(code) : '';
    return {
        errorText: message,
        errorCode: code,
        formatMessageId,
        name,
    };
};

const handleDataAccessErrors = (errorCode) => {
    if (errorCode === '403' || errorCode === '401') {
        return 'dataAccessNotAuthorized';
    } else if (errorCode === '404' || errorCode === '503') {
        return 'dataAccessDataNotLoaded';
    } else {
        return 'dataAccessGeneralErrorMessage';
    }
};

const graphApi = {
    cacheKey: 'Apply',
    webFetch: {
        graphQL: '/query',
        options: {
            headers: {
                'Content-Type': 'application/json'
            }
        }
    }
};

const localIntrospectionApi = {
    cacheKey: 'Apply',
    webFetch: {
        graphQL: '/query/graphqlIntrospection',
        options: {
            headers: {
                'Content-Type': 'application/json'
            }
        }
    }
};

export const api = {
    decisions: {
        getStatus: () => {
            return fetch('/api/decision/statuses', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        updateOfferStatusById: (application, offerStatus, applicantComment='') => {
            return fetch(`/api/decision/application/${application.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ offerStatus: offerStatus, offerComment: applicantComment }),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        }
    },
    payments: {
        getRedirectUrl: application => {
            return fetch('/api/eps/redirect', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({applicationId : application.id, callbackUrl : window.location.href.split('?')[0]}),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        getPaymentStatus: application => {
            return fetch(`/api/eps/status/${application.id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
    },
    mfaAuth: {
        checkUser: () => {
            return fetch('/api/totp/check-user', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        authUser: (otpSecret, token) => {
            return fetch('/api/totp/auth-user', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    otpSecret,
                    token
                }),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        userReset: (backupCode) => {
            return fetch('/api/totp/userReset', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    backupCode
                }),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        mfaReset: () => {
            return fetch('/api/totp/mfaReset', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            });
        },
    },
    applicationTemplates: {
        getById: id => {
            return fetch(`/api/applicationtemplates/template-id/${id}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        getAll: () => {
            return fetch('/api/applicationtemplates/summaries', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        getLatestPublishedTemplate: (originalTemplateId) => {
            return fetch(`/api/applicationtemplates/latest/${originalTemplateId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        }
    },
    applicationForms: {
        autosave: application => {
            return fetch(`/api/applications/autosave/${application.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(pick(application, ['id', 'revision', 'values', 'codes'])),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        create: (templateId, values = {}) => {
            return fetch('/api/applications', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    templateId,
                    values,
                }),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        delete: application => {
            return fetch(`/api/applications/${application.id}`, {
                method: 'DELETE'
            }).then(res => {
                if (res.ok) {
                    return Promise.resolve(true);
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        getById: id => {
            return fetch(`/api/applications/application-id/${id}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        validateApplication: async application => {
            return fetch('/api/applications/validate-application', {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json'
                },
                body: JSON.stringify(application),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        retrieve: () => {
            return new Promise((resolve, reject) => {
                const result = window.localStorage.getItem('applicant-apply-autosave');
                if (!result) {
                    reject(true);
                }
                resolve(JSON.parse(result));
            });
        },
        update: application => {
            return fetch('/api/applications', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(application),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        withdraw: id => {
            return fetch(`/api/applications/withdraw/${id}`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json'
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        savePayment: application => {
            return fetch(`/api/applications/payment/${application.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(application),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        validateDiscountCode: (code, id) => {
            return fetch(`/api/applications/${id}/validate-discount-code/${code}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        getAll: () => {
            return fetch('/api/applications/prospect-applications', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        checkForSubmittedPayment: id => {
            return fetch(`/api/applications/applicant-submitted-payment/${id}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        getCurriculumRules: async (term) => {
            let url = '/api/query/curriculum-rules';
            if (term) {
                url = `${url}?term=${term}`;
            }
            return fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
    },
    inquiries: {
        createInquiry: (inquiry, recaptchaToken, recaptchaAction) => {
            const params = qs.stringify({
                recaptcha_token: recaptchaToken,
                action: recaptchaAction,
            });
            return fetch(`/api/applications/inquiries?${params}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(inquiry),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        retrieve: async (model, recaptchaToken, recaptchaAction, bypass) => {
            const params = new URLSearchParams({
                recaptcha_token: recaptchaToken,
                action: recaptchaAction,
            });
            if (bypass) {
                params.set('bypass', 'true');
            }
            const url = `/api/applications/retrieve?${params.toString()}`;
            return fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(model),
            }).then(res => {
                if (res.ok) {
                    //App Retrieval never returns anything unless you are using the bypass but that is consumed in server.js
                    //     so don't call res.json () like we do elsewhere in this file
                    return true;
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        resendInquiryEmail: (inquiryId, locale) => {
            const url = `/api/applications/inquiries/resendEmail/${inquiryId}`;
            return fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ locale }),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
    },
    recaptchaToken: {
        get: () => {
            return fetch('/api/tenants/recaptcha-tokens', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
    },
    theme: {
        get: (themeName, appId = window.PRELOADED_STATE.themeEditorAppId) => {
            if (!appId || !themeName) {
                return Promise.reject('Ellucian Theme Editor app id and theme name must be provided');
            }
            const url = `${ellucianThemeEditor}/${appId}/theme/get?name=${themeName}`;
            return fetch(url, {
                method: 'GET',
            })
                .then(res => {
                    if (res.ok) {
                        return res.json();
                    }
                    throw res;
                })
                .catch(err => {
                    throw handleError(err);
                });
        },
    },
    dataAccess: {
        getRootTypes: async () => {
            try {
                const res = await fetchRootTypeNames(localIntrospectionApi);
                return res;
            }
            catch (err) {
                throw handleError(err, true);
            }
        },
        getTypes: async graphTypes => {
            try {
                await fetchRootTypeNames(localIntrospectionApi);
                const res = await fetchTypesByName(graphApi, graphTypes, true);
                return res;
            }
            catch (err) {
                throw handleError(err, true);
            }
        },
        getLookupValues: async (graphType, filter, sort, sortOptions = { rowsPerPage: 10000 }) => {
            try {
                graphType = graphType?.startsWith('F_') ? graphType.substring(2) : graphType;
                await fetchRootTypeNames(localIntrospectionApi);
                const res = await fetchEedmLookupValues(graphApi, graphType, filter, sort, sortOptions);
                return res;
            }
            catch (err) {
                throw handleError(err, true);
            }
        },
        getAttributeValues: async (graphType, attributes, filter, sort, sortOptions = { rowsPerPage: 10000 }, includeTotalCount = false) => {
            try {
                await fetchRootTypeNames(localIntrospectionApi);
                const res = await fetchEedmValuesByAttribute(graphApi, graphType, attributes, filter, sort, sortOptions, includeTotalCount);
                return res;
            }
            catch (err) {
                throw handleError(err, true);
            }
        },
        getMaxVersionByGraphType: async (graphType) => {
            try {
                graphType = !graphType?.startsWith('F_') ? 'F_' + graphType : graphType;
                await fetchRootTypeNames(localIntrospectionApi);
                const res = await getMaxVersionForBaseName(graphApi, graphType);
                return res;
            }
            catch (err) {
                throw await handleError(err, true);
            }
        },
        getValuesByIds: async (graphType, idList, attributes) => {
            try {
                await fetchRootTypeNames(localIntrospectionApi);
                const res = await fetchEedmValuesByIds(graphApi, graphType, idList, attributes);
                return res;
            }
            catch (err) {
                throw handleError(err, true);
            }
        },
        graphApi,
        localIntrospectionApi
    },
    communicate: {
        getVerifiedEmails: async () => {
            return fetch('/api/communicate/email-verifications/verified', {
                method: 'GET',
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            })
            .catch(async err => {
                throw await handleError(err);
            });
        }
    },
    applicantLogout: () => {
        return fetch('/applicantlogout', {
            method: 'GET'
        }).catch(err => {
            throw handleError(err);
        });
    }
};
const ApiContext = React.createContext(api);
const ApiProvider = props => {
    return (
        <ApiContext.Provider value={api}>{props.children}</ApiContext.Provider>
    );
};
ApiProvider.propTypes = {
    children: PropTypes.node,
};
const useApi = () => {
    const ctx = useContext(ApiContext);
    if (ctx === undefined) {
        throw new Error('useApi must be used within a ApiProvider');
    }
    return ctx;
};
export { ApiProvider, useApi };
